import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Typography, Box, Paper, IconButton, TextField, Autocomplete, Grid, ListSubheader, Collapse, Button } from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import AddIcon from '@mui/icons-material/Add'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { short, global as globalTr, menu as menuTr } from '../../../translate'
import { closestCorners, DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import FileCard from '../FileCard'
import {
    createEmptyMenuItem,
    getMenuArrayAtPath,
    setMenuChildrenAtPath,
    updateMenuItemAtPath,
} from './ivrMenuTreeUtils'

/** Background gets slightly darker with each nesting level (capped). */
function menuCardBackgroundByDepth(depth) {
    const stops = ['#ffffff', '#f3f4f6', '#e8eaef', '#dce0e8', '#cfd4de', '#c3c9d4']
    const i = Math.min(Math.max(depth, 0), stops.length - 1)
    return stops[i]
}

/** Child cards use a stronger theme border than the root row. */
function menuCardBorderByDepth(depth, primaryColor) {
    if (depth <= 0) return `1px solid ${primaryColor}40`
    if (depth === 1) return `1px solid ${primaryColor}5c`
    if (depth === 2) return `1px solid ${primaryColor}78`
    return `1px solid ${primaryColor}92`
}

function IvrMenuItemCard({ item, itemPath, depth, bodyExpanded, onToggleBodyExpand, ctx }) {
    const {
        setForm,
        langIndex,
        nameOptions,
        shortNumbers,
        menuConnectEmployees = [],
        loading,
        filesList,
        sensors,
        themeColors,
        inputStylesLocal,
        inputLabelStylesLocal,
        selectStylesLocal,
        group,
        weekDays,
        input,
        ivrSingle,
        primaryColor,
        hoverColor,
        groupPageStyles,
        sectionTitleStyles,
    } = ctx
    const [connectListGroupsExpanded, setConnectListGroupsExpanded] = useState({ add: true, employee: true, group: true })
    const keyLabel = String((itemPath[itemPath.length - 1] ?? 0) + 1)
    const pathToParentMenu = itemPath.slice(0, -1)

    const updateItem = (partial) => {
        setForm((prev) => ({
            ...prev,
            menu: updateMenuItemAtPath(prev.menu, itemPath, (n) => ({ ...n, ...partial })),
        }))
    }

    const setNodeFilesForm = (nf) => {
        setForm((prev) => ({
            ...prev,
            menu: updateMenuItemAtPath(prev.menu, itemPath, (n) => ({ ...n, files: nf.files })),
        }))
    }

    const connectOptionsFilter = useMemo(() => createFilterOptions(), [])
    const addSubmenuConnectOption = useMemo(
        () => ({
            _addSubmenu: true,
            id: '__ivr_add_submenu__',
            virtual: '__ADD_SUBMENU__',
            label: ivrSingle.connect_add_new_menu[langIndex],
        }),
        [langIndex, ivrSingle.connect_add_new_menu]
    )
    const menuConnectGroupsOnly = useMemo(
        () => shortNumbers.filter((s) => (s.isivr ?? 0) === 0),
        [shortNumbers]
    )
    const menuConnectThisNodeRow = useMemo(() => {
        const shortTrim = String(item.shortNumber || '').trim()
        if (!shortTrim) return []
        if (menuConnectGroupsOnly.some((s) => String(s.virtual) === shortTrim)) return []
        if (menuConnectEmployees.some((s) => String(s.virtual) === shortTrim)) return []
        return [
            {
                virtual: shortTrim,
                id: 'menu-node-current-short',
                name: shortTrim,
                label: `${shortTrim} (${langIndex === 2 ? 'Номер этого меню' : langIndex === 3 ? 'Այս մենյուի համար' : 'This menu'})`,
                isivr: 0,
                ivrMenuConnectSection: 'add',
            },
        ]
    }, [menuConnectGroupsOnly, menuConnectEmployees, item.shortNumber, langIndex])
    const menuConnectCoreOptions = useMemo(
        () => [...menuConnectThisNodeRow, ...menuConnectEmployees, ...menuConnectGroupsOnly],
        [menuConnectThisNodeRow, menuConnectEmployees, menuConnectGroupsOnly]
    )
    const connectAutocompleteOptions = useMemo(
        () => [addSubmenuConnectOption, ...menuConnectCoreOptions],
        [addSubmenuConnectOption, menuConnectCoreOptions]
    )

    const addNestedChild = () => {
        setForm((prev) => ({
            ...prev,
            menu: updateMenuItemAtPath(prev.menu, itemPath, (n) => ({
                ...n,
                filesUiVisible: true,
                menu: [...(n.menu || []), createEmptyMenuItem()],
            })),
        }))
    }

    const removeSelf = () => {
        const idx = itemPath[itemPath.length - 1]
        setForm((prev) => {
            const sib = getMenuArrayAtPath(prev.menu, pathToParentMenu)
            const next = sib.filter((_, i) => i !== idx)
            return { ...prev, menu: setMenuChildrenAtPath(prev.menu, pathToParentMenu, next) }
        })
    }

    const nodeFiles =
        Array.isArray(item.files) && item.files.length > 0
            ? item.files
            : [
                  {
                      id: `${item.id}-file0`,
                      file: '',
                      rx: '0',
                      tx: '0',
                      startDay: '2',
                      endDay: '1',
                      startHour: '00',
                      startMinute: '00',
                      endHour: '23',
                      endMinute: '59',
                      is_order: 0,
                  },
              ]

    return (
        <Paper
            elevation={0}
            sx={{
                p: depth > 0 ? 1.25 : 2,
                mb: 0,
                border: menuCardBorderByDepth(depth, primaryColor),
                backgroundColor: menuCardBackgroundByDepth(depth),
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: depth > 0 ? 1 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: depth > 0 ? 0.5 : 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    <IconButton
                        size="small"
                        onClick={onToggleBodyExpand}
                        aria-expanded={bodyExpanded}
                        sx={{ color: primaryColor, p: 0.5 }}
                        aria-label={bodyExpanded ? ivrSingle.collapse[langIndex] : ivrSingle.expand[langIndex]}
                    >
                        {bodyExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                    <Box
                        sx={{
                            minWidth: depth > 0 ? 32 : 40,
                            height: depth > 0 ? 32 : 40,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: depth > 0 ? '0.875rem' : '1rem',
                            bgcolor: `${primaryColor}18`,
                            color: primaryColor,
                            flexShrink: 0,
                        }}
                        aria-label={ivrSingle.press_key_label[langIndex]}
                    >
                        {keyLabel}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                        {ivrSingle.submenu_ivr_auto[langIndex]}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={removeSelf} sx={{ color: '#ef4444', flexShrink: 0 }} aria-label="Remove menu option">
                    <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 16 }} />
                </IconButton>
            </Box>
            <Collapse in={bodyExpanded}>
                <Grid container spacing={depth > 0 ? 1.5 : 2}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={nameOptions}
                            value={item.shortNumberName || ''}
                            onChange={(event, newValue) => {
                                const value = typeof newValue === 'string' ? newValue : newValue?.value || ''
                                updateItem({ shortNumberName: value })
                            }}
                            onInputChange={(event, newInputValue) => {
                                updateItem({ shortNumberName: newInputValue })
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={ivrSingle.short_number_name_label[langIndex]}
                                    placeholder={ivrSingle.short_number_name_placeholder[langIndex]}
                                    size="small"
                                    sx={inputStylesLocal}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={connectAutocompleteOptions}
                            filterOptions={(options, state) => {
                                const addOpt = options.find((o) => o && o._addSubmenu)
                                const rest = options.filter((o) => !(o && o._addSubmenu))
                                const filtered = connectOptionsFilter(rest, state)
                                return addOpt ? [addOpt, ...filtered] : filtered
                            }}
                            groupBy={(option) => {
                                if (!option || typeof option === 'string') return ''
                                if (option._addSubmenu) return ' '
                                if (option.ivrMenuConnectSection === 'add') return globalTr.add[langIndex]
                                if (option.ivrMenuConnectSection === 'employee') return menuTr.worker[langIndex]
                                return short.group[langIndex]
                            }}
                            renderGroup={(params) => {
                                if (params.group === ' ') {
                                    return (
                                        <li key={params.key}>
                                            <ul style={groupPageStyles.listUl}>{params.children}</ul>
                                        </li>
                                    )
                                }
                                if (params.group === '') {
                                    return (
                                        <li key={params.key}>
                                            <ul style={groupPageStyles.listUl}>{params.children}</ul>
                                        </li>
                                    )
                                }
                                const groupKey =
                                    params.group === globalTr.add[langIndex]
                                        ? 'add'
                                        : params.group === menuTr.worker[langIndex]
                                          ? 'employee'
                                          : 'group'
                                const expanded = connectListGroupsExpanded[groupKey] !== false
                                return (
                                    <li key={params.key}>
                                        <ListSubheader
                                            component="div"
                                            sx={groupPageStyles.listSubheader}
                                            onClick={() => setConnectListGroupsExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                        >
                                            {expanded ? <ExpandLessIcon sx={groupPageStyles.expandIcon} /> : <ExpandMoreIcon sx={groupPageStyles.expandIcon} />}
                                            {params.group}
                                        </ListSubheader>
                                        {expanded && <ul style={groupPageStyles.listUl}>{params.children}</ul>}
                                    </li>
                                )
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option
                                if (!option) return ''
                                return option.label || `${option.virtual}`
                            }}
                            renderOption={(props, option) =>
                                option && option._addSubmenu ? (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: primaryColor, fontWeight: 600 }}>
                                            <AddIcon sx={{ fontSize: 20 }} />
                                            {option.label}
                                        </Box>
                                    </li>
                                ) : (
                                    <li {...props}>{typeof option === 'string' ? option : option.label || option.virtual}</li>
                                )
                            }
                            value={(() => {
                                if (!item.noAnswerShort) return null
                                if (typeof item.noAnswerShort === 'object') return item.noAnswerShort
                                const matchingOption = menuConnectCoreOptions.find((s) => s.virtual === String(item.noAnswerShort))
                                return matchingOption || item.noAnswerShort
                            })()}
                            onChange={(event, newValue) => {
                                if (newValue && typeof newValue === 'object' && newValue._addSubmenu) {
                                    addNestedChild()
                                    return
                                }
                                updateItem({ noAnswerShort: newValue || '' })
                            }}
                            onInputChange={(event, newInputValue) => {
                                if (
                                    newInputValue &&
                                    !menuConnectCoreOptions.find((s) => s.virtual === newInputValue || s.label === newInputValue)
                                ) {
                                    updateItem({ noAnswerShort: newInputValue })
                                }
                            }}
                            isOptionEqualToValue={(option, value) => {
                                if (option && option._addSubmenu) return false
                                if (typeof option === 'string' && typeof value === 'string') return option === value
                                if (typeof option === 'object' && typeof value === 'object') return option.virtual === value.virtual
                                if (typeof option === 'object' && typeof value === 'string') return option.virtual === value
                                if (typeof option === 'string' && typeof value === 'object') return option === value.virtual
                                return false
                            }}
                            loading={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={ivrSingle.connect_to_what_label[langIndex]}
                                    placeholder={ivrSingle.connect_to_placeholder[langIndex]}
                                    size="small"
                                    sx={inputStylesLocal}
                                    InputLabelProps={{ sx: inputLabelStylesLocal }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                {item.filesUiVisible === true ? (
                    <Box sx={{ mt: depth > 0 ? 1 : 2, pt: 0, pl: 0, pr: 0 }}>
                        <Typography variant="caption" sx={{ ...sectionTitleStyles, mb: 0.5, display: 'block', fontWeight: 600 }}>
                            {ivrSingle.files_section[langIndex]}
                        </Typography>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCorners}
                            onDragEnd={(event) => {
                                const { active, over } = event
                                if (over && active.id !== over.id) {
                                    const oldIndex = nodeFiles.findIndex((f) => (f.id || nodeFiles.indexOf(f)) === active.id)
                                    const newIndex = nodeFiles.findIndex((f) => (f.id || nodeFiles.indexOf(f)) === over.id)
                                    if (oldIndex !== -1 && newIndex !== -1) {
                                        setNodeFilesForm({ files: arrayMove(nodeFiles, oldIndex, newIndex) })
                                    }
                                }
                            }}
                        >
                            <SortableContext items={nodeFiles.map((f) => f.id || nodeFiles.indexOf(f))} strategy={verticalListSortingStrategy}>
                                {nodeFiles.map((file, index) => (
                                    <FileCard
                                        key={`menu-file-${item.id}-${file.id || index}`}
                                        file={file}
                                        index={index}
                                        files={filesList}
                                        form={{ files: nodeFiles }}
                                        setForm={setNodeFilesForm}
                                        langIndex={langIndex}
                                        greenTheme={themeColors}
                                        inputStyles={inputStylesLocal}
                                        inputLabelStyles={inputLabelStylesLocal}
                                        selectStyles={selectStylesLocal}
                                        group={group}
                                        weekDays={weekDays}
                                        input={input}
                                        isOrdered={item.is_order === 1}
                                        compact
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                                setNodeFilesForm({
                                    files: [
                                        ...nodeFiles,
                                        {
                                            id: Date.now(),
                                            file: '',
                                            rx: '0',
                                            tx: '0',
                                            startDay: '2',
                                            endDay: '1',
                                            startHour: '00',
                                            startMinute: '00',
                                            endHour: '23',
                                            endMinute: '59',
                                            is_order: 0,
                                        },
                                    ],
                                })
                            }}
                            sx={{
                                ...groupPageStyles.addEmployeeButton,
                                mt: 0.5,
                                mb: 2,
                                py: 0.25,
                                minHeight: 32,
                                fontSize: '0.75rem',
                                backgroundColor: primaryColor,
                                color: '#fff',
                                '&:hover': { backgroundColor: hoverColor },
                            }}
                        >
                            {ivrSingle.add_employee[langIndex]}
                        </Button>
                    </Box>
                ) : null}
                <IvrMenuBranch
                    items={item.menu || []}
                    parentPath={itemPath}
                    depth={depth + 1}
                    parentFilesUiVisible={item.filesUiVisible === true}
                    ctx={ctx}
                />
            </Collapse>
        </Paper>
    )
}

/** parentPath = tree path to the node whose `menu` is `items` ([] = root list). */
export function IvrMenuBranch({ items, parentPath = [], depth, parentFilesUiVisible = false, ctx }) {
    const { setForm, langIndex, ivrSingle, primaryColor, hoverColor } = ctx
    const list = Array.isArray(items) ? items : []
    /** At most one sibling row expanded per branch (accordion). First child open by default. */
    const [openSiblingIndex, setOpenSiblingIndex] = useState(() => (list.length > 0 ? 0 : null))
    const prevListLenRef = useRef(-1)

    useEffect(() => {
        const n = list.length
        const prevLen = prevListLenRef.current
        prevListLenRef.current = n

        if (n === 0) {
            setOpenSiblingIndex(null)
            return
        }
        if (prevLen === 0 && n > 0) {
            setOpenSiblingIndex(0)
            return
        }
        setOpenSiblingIndex((prev) => {
            if (prev != null && prev >= n) return n - 1
            return prev
        })
    }, [list.length])

    const onAddSibling = () => {
        setForm((prev) => {
            const sib = getMenuArrayAtPath(prev.menu, parentPath) || []
            return { ...prev, menu: setMenuChildrenAtPath(prev.menu, parentPath, [...sib, createEmptyMenuItem()]) }
        })
    }
    const siblingGap = depth === 0 ? 2.5 : 3
    const showAddPressKey = depth === 0 || parentFilesUiVisible === true

    return (
        <Box
            sx={{
                mt: depth > 0 ? 2.5 : 0,
                pl: depth > 0 ? 1.5 : 0,
                ml: depth > 0 ? 0.5 : 0,
                borderLeft:
                    depth > 0
                        ? depth === 1
                            ? `2px solid ${primaryColor}38`
                            : depth === 2
                              ? `2px solid ${primaryColor}4d`
                              : `2px solid ${primaryColor}62`
                        : 'none',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: siblingGap }}>
                {list.map((item, index) => {
                    const itemPath = [...parentPath, index]
                    const pathKey = itemPath.length ? itemPath.join('-') : 'root'
                    return (
                        <IvrMenuItemCard
                            key={item.id != null && item.id !== '' ? String(item.id) : `mi-${pathKey}`}
                            item={item}
                            itemPath={itemPath}
                            depth={depth}
                            bodyExpanded={openSiblingIndex === index}
                            onToggleBodyExpand={() => {
                                setOpenSiblingIndex((prev) => (prev === index ? null : index))
                            }}
                            ctx={ctx}
                        />
                    )
                })}
            </Box>
            {showAddPressKey ? (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddSibling}
                    sx={{
                        mt: 2,
                        mb: depth === 0 ? 0 : 1,
                        textTransform: 'none',
                        borderColor: primaryColor,
                        color: primaryColor,
                        '&:hover': { borderColor: hoverColor, color: hoverColor },
                    }}
                >
                    {ivrSingle.add_press_key[langIndex]}
                </Button>
            ) : null}
        </Box>
    )
}
